import { paths } from '@luxuryescapes/contract-svc-merchandise'
import requestUtils from 'api/requestUtils'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import { convertPointsToPrice } from 'constants/merchandise'
import qs from 'qs'

const facetFilterMap = new Map<string, keyof App.MerchandiseFilters>([
  ['brand', 'brands'],
  ['category', 'categories'],
  ['price', 'price'],
  ['campaigns', 'campaigns'],
  ['tags', 'tags'],
])

type Response = paths['/api/merchandise/search/adverts/facets']['get']['responses']['200']['content']['application/json']

function getMerchandiseSearchFacets(
  filters: App.MerchandiseFilters = {},
): Promise<Array<App.MerchandiseAnySearchFacet>> {
  const mappedPriceMin = filters.price?.min ?? (filters.points?.min !== undefined ? convertPointsToPrice(filters.points.min) : undefined)
  const mappedPriceMax = filters.price?.max ?? (filters.points?.max !== undefined ? convertPointsToPrice(filters.points.max) : undefined)

  const query = qs.stringify({
    name: filters.name || undefined,
    categoryIds: filters.categories?.length ? filters.categories : undefined,
    brandIds: filters.brands?.length ? filters.brands : undefined,
    priceMin: mappedPriceMin ?? undefined,
    priceMax: mappedPriceMax ?? undefined,
    tags: filters.tags?.length ? filters.tags : undefined,
    // search facets endpoint assumes any other field is an option filter
    ...arrayToObject(filters.options ?? [], option => option.type, option => option.value),
  }, { arrayFormat: 'comma' })

  return requestUtils.get<Response>(`/api/merchandise/search/adverts/facets?${query}`, { excludeBrand: true }).then((response) => {
    const facets = response.result.facets.map((facet): App.MerchandiseAnySearchFacet | undefined => {
      const facetName = facet.name.toLowerCase()
      const mappedFilterName = facetFilterMap.get(facetName) ?? facetName
      // Skip points facet entirely
      if (mappedFilterName === 'points') return undefined
      if (facet.type === 'count') {
        return {
          type: 'count',
          filter: mappedFilterName,
          displayType: facet.displayType,
          values: facet.values.map((value) => ({
            label: value.label,
            value: value.value,
            count: value.count,
            hex: value.hex,
          })),
        }
        // make sure our range facet actually has a range!
      } else if (facet.type === 'range' && facet.max - facet.min > 0) {
        return {
          type: 'range',
          filter: mappedFilterName,
          min: facet.min ?? 0,
          max: facet.max ?? 0,
        }
      }

      return undefined
    })

    return nonNullable(facets)
  })
}

export default getMerchandiseSearchFacets
